import React, { useMemo, memo, useCallback } from 'react';
import * as R from 'ramda';
import classNames from 'classnames';
import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  useTheme,
  TextField,
  FormHelperText,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { BaseButton } from '@alycecom/ui';
import { useExternalErrors } from '@alycecom/hooks';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import { useSelector } from 'react-redux';

import { DECLINE_REASONS, ANOTHER_VARIANT, NOT_INTERESTING_MEETING } from '../../../constants/declineReasons.constants';
import Emoji from '../../../../../components/Emoji/Emoji';
import {
  TGiftDeclineForm,
  validationSchema,
  validationSchemaWithReasons,
} from '../../../store/decline/decline.schemas';
import { getGiftCustomizing, getGiftGl } from '../../../store/data/data.selectors';
import { getErrors, getIsLoading } from '../../../store/decline/decline.selectors';
import { getStatuses } from '../../../store/statuses/statuses.selectors';

const useStyles = makeStyles(({ palette, spacing }) => ({
  title: {
    fontSize: 32,
    fontWeight: 700,
  },
  subTitle: {
    fontSize: 20,
    fontWeight: 700,
    lineHeight: 1.24,
    marginBottom: spacing(3),
  },
  line: {
    width: 'calc(50% - 40px)',
  },
  circle: {
    fontWeight: 700,
  },
  hint: {
    fontSize: 12,
    color: palette.grey.main,
  },
  emoji: {
    color: palette.yellow.sunflower,
  },
  MuiFormControlLabelRoot: {
    fontSize: 16,
    fontWeight: 400,
    marginBottom: spacing(2),
  },
  MuiFormControlLabelLabel: {
    width: '100%',
  },
  MuiFormControlLabelWithField: {
    alignItems: 'flex-start',
  },
}));

const getDeclineReasonTitle = (reasonValue: string, reasons: { value: string; title: string }[]) =>
  reasons.find(({ value }) => value === reasonValue)?.title;

export interface IGiftDeclineFormProps {
  onSubmit: (reason: string) => void;
}

const GiftDeclineForm = ({ onSubmit }: IGiftDeclineFormProps) => {
  const classes = useStyles();
  const { palette } = useTheme();

  const gl = useSelector(getGiftGl);
  const customizing = useSelector(getGiftCustomizing);
  const isLoading = useSelector(getIsLoading);
  const statuses = useSelector(getStatuses);
  const externalErrors = useSelector(getErrors);

  const hideReasons = customizing && customizing.hidePrefilledReasons;
  const hasMeeting = statuses.meeting;

  const schema = useMemo(() => (hideReasons ? validationSchema : validationSchemaWithReasons), [hideReasons]);

  const { errors, handleSubmit, control, setValue, setError, clearErrors } = useForm<TGiftDeclineForm>({
    mode: 'all',
    defaultValues: {
      reason: '',
      declineReason: DECLINE_REASONS[0].value,
    },
    resolver: yupResolver(schema),
  });

  const declineReasons = useMemo(
    () => (hasMeeting ? DECLINE_REASONS : R.reject(R.propEq('value', NOT_INTERESTING_MEETING), DECLINE_REASONS)),
    [hasMeeting],
  );

  const handleSubmitForm = useCallback(
    ({ declineReason, reason }) => {
      const reasonValue =
        declineReason === ANOTHER_VARIANT || hideReasons
          ? reason
          : getDeclineReasonTitle(declineReason, DECLINE_REASONS);
      onSubmit(reasonValue);
    },
    [onSubmit, hideReasons],
  );

  useExternalErrors<TGiftDeclineForm>(setError, externalErrors);

  return (
    <form onSubmit={handleSubmit(handleSubmitForm)}>
      <Box mt={8} mb={5} mx={[3, 'auto']} color="text.primary" textAlign="center">
        <Typography data-testid="giftDeclineHeader" className={classes.title}>
          We’re sorry to see you go... <Emoji emoji="cryingFace" />
        </Typography>
      </Box>
      <Box color="text.primary" textAlign="left" width="auto" maxWidth={620} mx={[3, 'auto']}>
        <Typography className={classes.subTitle}>Tell us why you’re declining your {gl}:</Typography>
        {hideReasons ? (
          <Controller
            control={control}
            name="reason"
            render={({ onChange, value }) => (
              <TextField
                id="field-reason-id"
                name="reason"
                variant="outlined"
                label="Decline reason"
                placeholder="Decline reason"
                multiline
                rows={5}
                fullWidth
                value={value}
                error={!!errors.reason}
                helperText={errors.reason && errors.reason.message}
                onChange={onChange}
                disabled={isLoading}
              />
            )}
          />
        ) : (
          <Controller
            name="declineReason"
            control={control}
            render={({ onChange, value }) => (
              <>
                <RadioGroup
                  aria-label="declineReason"
                  name="declineReason"
                  value={value}
                  onChange={event => {
                    onChange(event.target.value);
                    if (event.target.value !== ANOTHER_VARIANT) {
                      setValue('reason', '', { shouldDirty: false });
                      clearErrors('reason');
                    }
                  }}
                >
                  {declineReasons.map(item => (
                    <FormControlLabel
                      key={item.value}
                      data-testid={`declineReasonControlLabel-${item.value}`}
                      value={item.value}
                      control={<Radio color="primary" />}
                      classes={{
                        root: classes.MuiFormControlLabelRoot,
                        label: 'controlLabel-label-ie',
                      }}
                      label={item.title}
                      disabled={isLoading}
                    />
                  ))}
                  <FormControlLabel
                    data-testid="declineControlLabel"
                    value={ANOTHER_VARIANT}
                    control={<Radio color="primary" />}
                    classes={{
                      root: classNames(classes.MuiFormControlLabelRoot, classes.MuiFormControlLabelWithField),
                      label: classes.MuiFormControlLabelLabel,
                    }}
                    disabled={isLoading}
                    label={
                      <Controller
                        control={control}
                        name="reason"
                        render={({ onChange: onChangeReason, value: reasonValue }) => (
                          <TextField
                            id="field-reason-id"
                            name="reason"
                            variant="outlined"
                            label="Other (please let us know why)"
                            placeholder="Other (please let us know why)"
                            fullWidth
                            error={!!errors.reason}
                            helperText={errors.reason && errors.reason.message}
                            disabled={value !== ANOTHER_VARIANT || isLoading}
                            value={reasonValue}
                            onChange={onChangeReason}
                          />
                        )}
                      />
                    }
                  />
                </RadioGroup>
                {!!errors.declineReason && (
                  <FormHelperText className="Body-Small-Error">
                    {errors.declineReason && errors.declineReason.message}
                  </FormHelperText>
                )}
              </>
            )}
          />
        )}
      </Box>
      <Box my={2} mx={[2, 'auto']} px={[2, 10]} maxWidth={620}>
        <BaseButton
          type="submit"
          overrideClass="submitButton-ie"
          data-testid="declineThisGiftButton"
          width="100%"
          variant="outlined"
          color={palette.common.white}
          hoverColor={palette.common.white}
          bgColor={palette.error.main}
          hoverBgColor={palette.error.dark}
          borderColor={palette.error.main}
          disabled={isLoading}
        >
          Decline this {gl}
        </BaseButton>
      </Box>
    </form>
  );
};

export default memo(GiftDeclineForm);
